import React, { useEffect, useState } from 'react';
import { useGoals } from '../lib/stores/goals';
import { useLifeAreas } from '../lib/stores/lifeareas';
import { useVision } from '../lib/stores/vision';
import { useValues } from '../lib/stores/values';
import { useIkigai } from '../lib/stores/ikigai';
import { useCrypto } from '../lib/stores/crypto';
import Button from '../lib/design-system/components/Button';
import Card from '../lib/design-system/components/Card';

export default function GoalsPage() {
  const crypto = useCrypto();
  const { goals, activeGoals, completedGoals, loading, error, initialize, createGoal, updateGoal, deleteGoal, initialized } = useGoals();
  const { areas, initialize: initAreas, initialized: areasInitialized } = useLifeAreas();
  const { visions, getVisionsByArea, initialize: initVisions, initialized: visionsInitialized } = useVision();
  const { values, getValuesByArea, initialize: initValues, initialized: valuesInitialized } = useValues();
  const { statement: ikigaiStatement, initialize: initIkigai, initialized: ikigaiInitialized } = useIkigai();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterArea, setFilterArea] = useState<string | null>(null);
  const [showFoundation, setShowFoundation] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    targetDate: '',
    lifeAreaId: '',
  });

  useEffect(() => {
    // Only initialize when encryption key is ready
    if (!crypto.encryptionKey) return;

    if (!initialized) initialize();
    if (!areasInitialized) initAreas();
    if (!visionsInitialized) initVisions();
    if (!valuesInitialized) initValues();
    if (!ikigaiInitialized) initIkigai();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crypto.encryptionKey, initialized, areasInitialized, visionsInitialized, valuesInitialized, ikigaiInitialized]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createGoal({
        title: formData.title,
        description: formData.description || undefined,
        targetDate: formData.targetDate || undefined,
        lifeAreaId: formData.lifeAreaId || undefined,
      });
      setFormData({ title: '', description: '', targetDate: '', lifeAreaId: '' });
      setShowCreateForm(false);
    } catch (err) {
      console.error('Failed to create goal:', err);
    }
  };

  const handleCreateFromVision = (visionText: string, areaId: string) => {
    setFormData({
      title: '',
      description: `Vision: ${visionText}`,
      targetDate: '',
      lifeAreaId: areaId,
    });
    setShowCreateForm(true);
    // Scroll to form
    setTimeout(() => {
      document.querySelector('form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleStatusToggle = async (goalId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'completed' : 'active';
    await updateGoal(goalId, { status: newStatus as any });
  };

  const filteredGoals = filterArea
    ? goals.filter(g => g.life_area_id === filterArea)
    : goals;

  const getAreaName = (areaId: string | null) => {
    if (!areaId) return 'No Area';
    const area = areas.find(a => a.id === areaId);
    return area ? area.name : 'Unknown Area';
  };

  const getAreaColor = (areaId: string | null) => {
    if (!areaId) return '#6B7280';
    const area = areas.find(a => a.id === areaId);
    return area ? area.color : '#6B7280';
  };

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading goals...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
          fontWeight: 400,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.5rem',
        }}>
          Goals
        </h1>
        <p style={{
          color: 'var(--color-neutral-600)',
          fontSize: 'clamp(1rem, 2vw, 1.125rem)',
        }}>
          Set and track your SMART goals aligned with your foundation
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1rem',
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
      }}>
        <button
          onClick={() => setFilterArea(null)}
          style={{
            padding: '0.5rem 1rem',
            border: filterArea === null ? '2px solid var(--color-primary-600)' : '1px solid var(--color-neutral-300)',
            borderRadius: '8px',
            background: filterArea === null ? 'var(--color-primary-50)' : 'white',
            color: filterArea === null ? 'var(--color-primary-700)' : 'var(--color-neutral-700)',
            fontWeight: filterArea === null ? 600 : 400,
            cursor: 'pointer',
          }}
        >
          All ({goals.length})
        </button>
        {areas.map(area => (
          <button
            key={area.id}
            onClick={() => setFilterArea(area.id)}
            style={{
              padding: '0.5rem 1rem',
              border: filterArea === area.id ? `2px solid ${area.color}` : '1px solid var(--color-neutral-300)',
              borderRadius: '8px',
              background: filterArea === area.id ? `${area.color}15` : 'white',
              color: filterArea === area.id ? area.color : 'var(--color-neutral-700)',
              fontWeight: filterArea === area.id ? 600 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span>{area.icon}</span>
            <span>{area.name}</span>
          </button>
        ))}
      </div>

      {/* Foundation Insights */}
      {(filterArea || ikigaiStatement || values.length > 0) && (
        <Card style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: 'var(--color-primary-50)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--color-neutral-900)',
            }}>
              💡 Foundation Guidance
            </h3>
            <button
              onClick={() => setShowFoundation(!showFoundation)}
              style={{
                padding: '0.5rem',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '1.25rem',
              }}
            >
              {showFoundation ? '−' : '+'}
            </button>
          </div>

          {showFoundation && (
            <>
              {/* Ikigai */}
              {ikigaiStatement && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                    🎯 Your Ikigai
                  </div>
                  <p style={{ color: 'var(--color-neutral-700)', fontStyle: 'italic', lineHeight: '1.6' }}>
                    "{ikigaiStatement.statement}"
                  </p>
                </div>
              )}

              {/* Area-Specific Values or Top Values */}
              {(() => {
                const displayValues = filterArea && getValuesByArea ? getValuesByArea(filterArea) : (values || []).slice(0, 5);
                return displayValues && displayValues.length > 0 ? (
                  <div style={{ marginBottom: filterArea ? '1.5rem' : '0' }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      💎 {filterArea ? `Values for ${getAreaName(filterArea)}` : 'Your Top Values'}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {displayValues.map((value, idx) => (
                        <span
                          key={value.id}
                          style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            backgroundColor: 'var(--color-primary-100)',
                            color: 'var(--color-primary-700)',
                            fontWeight: 500,
                          }}
                        >
                          {!filterArea && `${idx + 1}. `}{value.value_name}
                        </span>
                      ))}
                    </div>
                    {filterArea && displayValues.length === 0 && (
                      <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-500)', fontStyle: 'italic' }}>
                        No values mapped to this area yet. Add value mappings in the Values page.
                      </p>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Existing Goals for Area */}
              {filterArea && (() => {
                const areaGoals = goals.filter(g => g.life_area_id === filterArea);
                return areaGoals.length > 0 ? (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      🎯 Current Goals in {getAreaName(filterArea)} ({areaGoals.length})
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
                      {areaGoals.filter(g => g.status === 'active').length} active, {areaGoals.filter(g => g.status === 'completed').length} completed
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Area-Specific Visions */}
              {filterArea && (() => {
                const areaVisions = getVisionsByArea(filterArea);
                return areaVisions.length > 0 ? (
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: '0.75rem', fontSize: '0.875rem' }}>
                      🔮 Vision for {getAreaName(filterArea)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {areaVisions.map(vision => (
                        <div
                          key={vision.id}
                          style={{
                            padding: '1rem',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid var(--color-primary-200)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-primary-600)', marginBottom: '0.25rem' }}>
                                {vision.timeframe.toUpperCase()}
                              </div>
                              <p style={{ color: 'var(--color-neutral-700)', lineHeight: '1.6', marginBottom: '0.5rem' }}>
                                {vision.vision_text}
                              </p>
                            </div>
                            <Button
                              onClick={() => handleCreateFromVision(vision.vision_text, filterArea)}
                              style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                flexShrink: 0,
                              }}
                            >
                              Create Goal
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </>
          )}
        </Card>
      )}

      {/* Create Button */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          {showCreateForm ? 'Cancel' : '+ Create Goal'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card style={{ marginBottom: '2rem', padding: '1.5rem' }}>
          <form onSubmit={handleCreate}>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.5rem',
              marginBottom: '1rem',
            }}>
              Create New Goal
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
              }}>
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
              }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                }}>
                  Life Area
                </label>
                <select
                  value={formData.lifeAreaId}
                  onChange={(e) => setFormData({ ...formData, lifeAreaId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                >
                  <option value="">No Area</option>
                  {areas.map(area => (
                    <option key={area.id} value={area.id}>
                      {area.icon} {area.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                }}>
                  Target Date
                </label>
                <input
                  type="date"
                  value={formData.targetDate}
                  onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button type="submit" disabled={!formData.title.trim()}>
                Create Goal
              </Button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Goals Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 'clamp(0.75rem, 1.5vw, 1rem)', marginBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
        <Card style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-primary-600)' }}>
            {activeGoals.length}
          </div>
          <div style={{ color: 'var(--color-neutral-600)', marginTop: '0.25rem' }}>
            Active Goals
          </div>
        </Card>
        <Card style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-success-600)' }}>
            {completedGoals.length}
          </div>
          <div style={{ color: 'var(--color-neutral-600)', marginTop: '0.25rem' }}>
            Completed
          </div>
        </Card>
      </div>

      {/* Goals List */}
      <div className="grid-standard grid-equal-height">
        {filteredGoals.length === 0 ? (
          <Card style={{ padding: '3rem', textAlign: 'center', gridColumn: '1 / -1' }}>
            <p style={{ color: 'var(--color-neutral-500)' }}>
              {filterArea ? 'No goals in this area yet.' : 'No goals yet. Create your first goal to get started!'}
            </p>
          </Card>
        ) : (
          filteredGoals.map(goal => (
            <Card key={goal.id} className="card-compact-desktop">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    textDecoration: goal.status === 'completed' ? 'line-through' : 'none',
                    color: goal.status === 'completed' ? 'var(--color-neutral-500)' : 'var(--color-neutral-900)',
                  }}>
                    {goal.title}
                  </h3>
                  {goal.description && (
                    <p style={{ color: 'var(--color-neutral-600)', marginBottom: '0.75rem' }}>
                      {goal.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    {goal.life_area_id && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        backgroundColor: `${getAreaColor(goal.life_area_id)}15`,
                        color: getAreaColor(goal.life_area_id),
                        fontWeight: 500,
                      }}>
                        {getAreaName(goal.life_area_id)}
                      </span>
                    )}
                    {goal.target_date && (
                      <span style={{ fontSize: '0.875rem', color: 'var(--color-neutral-500)' }}>
                        Target: {new Date(goal.target_date).toLocaleDateString()}
                      </span>
                    )}
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: goal.status === 'completed' ? 'var(--color-success-600)' : 'var(--color-primary-600)',
                    }}>
                      {goal.progress}% Complete
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleStatusToggle(goal.id, goal.status)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '6px',
                      background: goal.status === 'completed' ? 'var(--color-neutral-200)' : 'var(--color-success-600)',
                      color: goal.status === 'completed' ? 'var(--color-neutral-700)' : 'white',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    {goal.status === 'completed' ? 'Reopen' : 'Complete'}
                  </button>
                  <button
                    onClick={() => deleteGoal(goal.id)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid var(--color-error-300)',
                      borderRadius: '6px',
                      background: 'white',
                      color: 'var(--color-error-600)',
                      cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{
                height: '8px',
                backgroundColor: 'var(--color-neutral-200)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${goal.progress}%`,
                  backgroundColor: goal.status === 'completed' ? 'var(--color-success-600)' : 'var(--color-primary-600)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
